'use client';

import { Badge, Box, Button, Flex, HStack, Image, Link, Text, VStack } from '@chakra-ui/react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleX,
  Columns2,
  ExternalLink,
  FileDiff,
  GitPullRequest,
  Rows3,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Diff,
  Hunk,
  markEdits,
  parseDiff,
  tokenize,
  type DiffType,
  type ViewType,
} from 'react-diff-view';
import 'react-diff-view/style/index.css';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { Tooltip } from '@/components/ui/tooltip';
import { notifyDeliveryApprovalsChanged } from '@/lib/delivery-approvals/client-events';
import { GitHubPreviewSeoReview } from './GitHubPreviewSeoReview';

interface PreviewChangeSummary {
  operation: 'create' | 'update' | 'delete';
  path: string;
  kind: 'text' | 'binary';
  additions: number;
  deletions: number;
  beforePreviewUrl?: string | null;
  afterPreviewUrl?: string | null;
  beforeContentType?: string | null;
  beforeSizeBytes?: number | null;
  beforeWidth?: number | null;
  beforeHeight?: number | null;
  afterContentType?: string | null;
  afterSizeBytes?: number | null;
  afterWidth?: number | null;
  afterHeight?: number | null;
}

export interface GitHubPreviewChangesResponse {
  projectId: string;
  preview: {
    id: string;
    revision: number;
    validatedRevision: number | null;
    manifestSha256: string;
    validationStatus: string | null;
    status: string;
  };
  summary: {
    files: number;
    textFiles: number;
    mediaFiles: number;
    additions: number;
    deletions: number;
  };
  files: PreviewChangeSummary[];
  delivery: {
    state: string;
    deliveredRevision: number | null;
    approvalId: string | null;
    approvalStatus: string | null;
    pullRequestNumber: number | null;
    pullRequestUrl: string | null;
    headSha: string | null;
    error: string | null;
  };
  permissions: { canDeliver: boolean };
}

interface PreviewChangeDetailResponse {
  preview: GitHubPreviewChangesResponse['preview'];
  change: PreviewChangeSummary & { diff: string };
}

function safePullRequestUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'github.com' ? url.toString() : null;
  } catch {
    return null;
  }
}

function imageMetadata(change: PreviewChangeSummary, side: 'before' | 'after') {
  const prefix = side === 'before' ? 'before' : 'after';
  const width = change[`${prefix}Width`];
  const height = change[`${prefix}Height`];
  const bytes = change[`${prefix}SizeBytes`];
  const type = change[`${prefix}ContentType`];
  return [
    type || null,
    width && height ? `${width} × ${height}px` : null,
    bytes ? `${(bytes / 1024).toFixed(1)} KiB` : null,
  ].filter(Boolean);
}

function BinaryChange({ change }: { change: PreviewChangeSummary }) {
  const images = [
    { label: 'Before', src: change.beforePreviewUrl, metadata: imageMetadata(change, 'before') },
    { label: 'After', src: change.afterPreviewUrl, metadata: imageMetadata(change, 'after') },
  ].filter(image => image.src);
  return (
    <VStack align="stretch" gap="3" p="3" bg="ui.surface.card">
      {images.map(image => (
        <Box key={image.label}>
          <Text mb="1.5" fontSize="10px" fontWeight="800" color="ui.text.tertiary">
            {image.label}
          </Text>
          <Image
            src={image.src || undefined}
            alt={`${image.label} image at ${change.path}`}
            maxH="260px"
            w="full"
            objectFit="contain"
            borderRadius="md"
            bg="ui.surface.drawerMuted"
          />
          <HStack mt="2" gap="1.5" flexWrap="wrap">
            {image.metadata.map(item => (
              <Badge key={item} size="sm" variant="subtle">
                {item}
              </Badge>
            ))}
          </HStack>
        </Box>
      ))}
    </VStack>
  );
}

function diffTypeForOperation(operation: PreviewChangeSummary['operation']): DiffType {
  if (operation === 'create') return 'add';
  if (operation === 'delete') return 'delete';
  return 'modify';
}

function operationLabel(operation: PreviewChangeSummary['operation']) {
  if (operation === 'create') return 'Added';
  if (operation === 'delete') return 'Deleted';
  return 'Modified';
}

function operationPalette(operation: PreviewChangeSummary['operation']) {
  if (operation === 'create') return 'green';
  if (operation === 'delete') return 'red';
  return 'gray';
}

function parseChangeDiff(path: string, diff: string) {
  if (!diff.trim()) return null;
  try {
    const source = diff.startsWith('diff --git ')
      ? diff
      : `diff --git a/${path} b/${path}\n${diff}`;
    return parseDiff(source, { nearbySequences: 'zip' })[0] || null;
  } catch {
    return null;
  }
}

function TextChangeDiff({
  change,
  viewType,
}: {
  change: PreviewChangeSummary & { diff: string };
  viewType: ViewType;
}) {
  const file = useMemo(() => parseChangeDiff(change.path, change.diff), [change.diff, change.path]);
  const tokens = useMemo(() => {
    if (!file) return null;
    try {
      return tokenize(file.hunks, {
        enhancers: [markEdits(file.hunks, { type: 'line' })],
      });
    } catch {
      return null;
    }
  }, [file]);

  if (!change.diff) {
    return (
      <Text p="3" borderTopWidth="1px" borderColor="ui.border.subtle" fontSize="xs">
        No textual diff was recorded.
      </Text>
    );
  }

  if (!file) {
    return (
      <Box
        as="pre"
        m="0"
        p="3"
        maxH="520px"
        overflow="auto"
        borderTopWidth="1px"
        borderColor="ui.border.subtle"
        fontFamily="mono"
        fontSize="11px"
        lineHeight="1.55"
        whiteSpace="pre"
      >
        {change.diff}
      </Box>
    );
  }

  return (
    <Box
      maxH="520px"
      overflow="auto"
      borderTopWidth="1px"
      borderColor="ui.border.subtle"
      bg="ui.surface.card"
      overscrollBehavior="contain"
      css={{
        '& .diff': {
          minWidth: viewType === 'split' ? '920px' : '640px',
          background: 'ui.surface.card',
          color: 'ui.text.primary',
        },
        '& .diff td': {
          minWidth: 0,
        },
        '& .diff-line': {
          fontFamily: 'mono',
          fontSize: '11px',
          lineHeight: '1.6',
        },
        '& .diff-code': {
          minWidth: 0,
          maxWidth: viewType === 'split' ? 0 : 'none',
          px: '2.5',
          whiteSpace: viewType === 'split' ? 'pre-wrap' : 'pre',
          wordBreak: viewType === 'split' ? 'break-word' : 'normal',
          overflowWrap: viewType === 'split' ? 'anywhere' : 'normal',
          overflow: 'hidden',
        },
        '& .diff-gutter': {
          width: '6ch',
          minWidth: '6ch',
          px: '1.5',
          color: 'ui.text.tertiary',
          borderRightWidth: '1px',
          borderColor: 'ui.border.subtle',
        },
        '& .diff-gutter-insert, & .diff-code-insert': {
          background: 'status.success.bg',
        },
        '& .diff-gutter-delete, & .diff-code-delete': {
          background: 'status.danger.bg',
        },
        '& .diff-code-insert .diff-code-edit': {
          background: 'status.success.border',
        },
        '& .diff-code-delete .diff-code-edit': {
          background: 'status.danger.border',
        },
        '& .diff-decoration': {
          background: 'ui.surface.drawerMuted',
          color: 'ui.text.secondary',
        },
      }}
    >
      <Diff
        viewType={viewType}
        diffType={diffTypeForOperation(change.operation)}
        hunks={file.hunks}
        tokens={tokens}
      >
        {hunks => hunks.map(hunk => <Hunk key={hunk.content} hunk={hunk} />)}
      </Diff>
    </Box>
  );
}

export function GitHubPreviewChangesPanel({
  previewId,
  summary,
  onSummaryChange,
  onSummaryRefresh,
  onBack,
}: {
  previewId: string;
  summary: GitHubPreviewChangesResponse;
  onSummaryChange: (summary: GitHubPreviewChangesResponse) => void;
  onSummaryRefresh: () => Promise<GitHubPreviewChangesResponse | null>;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const [expandedPath, setExpandedPath] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, PreviewChangeDetailResponse>>({});
  const [loadingPath, setLoadingPath] = useState<string | null>(null);
  const [delivering, setDelivering] = useState(false);
  const [preferredViewType, setPreferredViewType] = useState<ViewType>('split');
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const supportsSplitView = panelWidth >= 960;
  const viewType: ViewType = supportsSplitView ? preferredViewType : 'unified';

  useEffect(() => {
    const element = panelRef.current;
    if (!element) return;
    const updateWidth = () => setPanelWidth(element.getBoundingClientRect().width);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const path = expandedPath;
    if (!path || !user) return;
    if (!summary.files.some(change => change.path === path)) {
      setExpandedPath(null);
      return;
    }
    const detailKey = `${summary.preview.revision}:${path}`;
    if (details[detailKey]) return;
    let cancelled = false;
    const fetchDetail = async (revision: number, token: string) => {
      const query = new URLSearchParams({ revision: String(revision), path });
      const response = await fetch(
        `/api/github-previews/${encodeURIComponent(previewId)}/changes?${query}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
      );
      const payload = (await response.json().catch(() => ({}))) as PreviewChangeDetailResponse & {
        error?: string;
        code?: string;
      };
      return { response, payload };
    };
    void (async () => {
      setLoadingPath(path);
      try {
        const token = await user.getIdToken();
        const result = await fetchDetail(summary.preview.revision, token);
        if (result.response.status === 409 && result.payload.code === 'GITHUB_PREVIEW_STALE') {
          const current = await onSummaryRefresh();
          if (!current || !current.files.some(change => change.path === path)) return;
          return;
        }
        if (!result.response.ok || !result.payload.change || !result.payload.preview) {
          throw new Error(result.payload.error || 'Change details are unavailable');
        }
        if (!cancelled) {
          const currentKey = `${result.payload.preview.revision}:${path}`;
          setDetails(current => ({ ...current, [currentKey]: result.payload }));
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : 'Change details are unavailable');
        }
      } finally {
        if (!cancelled) setLoadingPath(current => (current === path ? null : current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    details,
    expandedPath,
    onSummaryRefresh,
    previewId,
    summary.files,
    summary.preview.revision,
    user,
  ]);

  const togglePath = (path: string) => {
    const next = expandedPath === path ? null : path;
    setExpandedPath(next);
  };

  const pullRequestUrl = safePullRequestUrl(summary.delivery.pullRequestUrl);
  const noChanges = summary.summary.files === 0;
  const buildBlocked =
    summary.preview.validationStatus !== 'passed' ||
    summary.preview.validatedRevision !== summary.preview.revision ||
    summary.preview.revision < 1 ||
    ['validating', 'invalid', 'failed'].includes(summary.preview.status);
  const buildLabel =
    summary.preview.validationStatus === 'failed'
      ? 'Build failed'
      : !buildBlocked
        ? 'Build passed'
        : summary.preview.status === 'validating'
          ? 'Building'
          : 'Build required';
  const currentPullRequest =
    summary.delivery.state === 'pr_open' &&
    summary.delivery.deliveredRevision === summary.preview.revision;
  const pendingRequest = summary.delivery.state === 'approval_pending';
  const reviewExecuting = ['executing', 'reconciliation_required'].includes(summary.delivery.state);
  const action = summary.permissions.canDeliver ? 'create_or_update_pr' : 'request_approval';
  const actionLabel = summary.permissions.canDeliver
    ? pullRequestUrl
      ? 'Update pull request'
      : 'Create pull request'
    : summary.delivery.state === 'approval_outdated'
      ? 'Update approval request'
      : 'Send for approval';
  const blockedReason = noChanges
    ? 'No changes to deliver.'
    : buildBlocked
      ? summary.preview.validationStatus === 'failed'
        ? 'Fix the failed build before delivery.'
        : summary.preview.status === 'validating'
          ? 'Wait for the current revision to finish building.'
          : 'Open Review & submit to build the exact current revision.'
      : currentPullRequest
        ? 'The pull request contains this exact revision.'
        : reviewExecuting
          ? 'This change set is already being delivered. Wait for its final receipt.'
          : pendingRequest && !summary.permissions.canDeliver
            ? 'The cumulative change set is waiting for manager review.'
            : summary.delivery.error;

  const deliver = async () => {
    if (!user || noChanges || buildBlocked || currentPullRequest || reviewExecuting || delivering)
      return;
    setDelivering(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/github-previews/${encodeURIComponent(previewId)}/delivery`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            revision: summary.preview.revision,
            manifestSha256: summary.preview.manifestSha256,
            confirmed: true,
            idempotencyKey: crypto.randomUUID(),
          }),
        }
      );
      const payload = (await response.json().catch(() => ({}))) as {
        changes?: GitHubPreviewChangesResponse;
        permissions?: { canDeliver: boolean };
        reconciliationRequired?: boolean;
        error?: string;
      };
      if (!response.ok || !payload.changes) {
        throw new Error(payload.error || 'The cumulative change set could not be delivered');
      }
      const next = {
        ...payload.changes,
        permissions: payload.permissions || summary.permissions,
      };
      onSummaryChange(next);
      notifyDeliveryApprovalsChanged(next.projectId);
      if (payload.reconciliationRequired) {
        toast.warning(
          'The pull request was delivered, but Attune still needs to synchronize the linked task.'
        );
        return;
      }
      toast.success(
        action === 'request_approval'
          ? 'Sent for approval.'
          : pullRequestUrl
            ? 'Pull request updated.'
            : 'Pull request created.'
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Delivery failed');
    } finally {
      setDelivering(false);
    }
  };

  return (
    <Flex ref={panelRef} direction="column" h="full" minH={0} bg="ui.surface.drawer">
      <Box
        position="sticky"
        top="0"
        zIndex="1"
        px="3"
        py="2"
        borderBottomWidth="1px"
        borderColor="ui.border.subtle"
        bg="ui.surface.drawerHeader"
      >
        <HStack minH="8" gap="2.5">
          <Button size="xs" variant="ghost" px="2" onClick={onBack}>
            <ArrowLeft size={13} aria-hidden="true" />
            Preview
          </Button>
          <Box h="4" w="1px" bg="ui.border.subtle" />
          <Text fontSize="xs" fontWeight="750" whiteSpace="nowrap">
            {summary.summary.files} {summary.summary.files === 1 ? 'file' : 'files'} updated
          </Text>
          <Text fontSize="2xs" color="ui.text.tertiary" whiteSpace="nowrap">
            +{summary.summary.additions} −{summary.summary.deletions} · R{summary.preview.revision}
          </Text>
          <Box flex="1" />
          {pullRequestUrl ? (
            <Link
              href={pullRequestUrl}
              target="_blank"
              rel="noreferrer"
              display="inline-flex"
              alignItems="center"
              gap="1"
              px="2"
              h="7"
              borderRadius="md"
              fontSize="2xs"
              fontWeight="700"
              _hover={{ bg: 'ui.surface.drawerMuted', textDecoration: 'none' }}
            >
              <GitPullRequest size={12} aria-hidden="true" />
              PR #{summary.delivery.pullRequestNumber}
              <ExternalLink size={12} aria-hidden="true" />
            </Link>
          ) : null}
          <Tooltip content={`${buildLabel} for revision ${summary.preview.revision}`}>
            <Box
              as="span"
              role="status"
              aria-label={buildLabel}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="7"
              h="7"
              color={
                summary.preview.validationStatus === 'failed'
                  ? 'status.danger.fg'
                  : buildBlocked
                    ? 'status.warning.fg'
                    : 'status.success.fg'
              }
            >
              {summary.preview.validationStatus === 'failed' ? (
                <CircleX size={15} aria-hidden="true" />
              ) : buildBlocked ? (
                <Spinner size="xs" />
              ) : (
                <CheckCircle2 size={15} aria-hidden="true" />
              )}
            </Box>
          </Tooltip>
          {supportsSplitView && summary.summary.textFiles > 0 ? (
            <HStack
              role="group"
              aria-label="Diff layout"
              gap="0.5"
              p="0.5"
              borderWidth="1px"
              borderColor="ui.border.subtle"
              borderRadius="md"
              bg="ui.surface.drawerMuted"
            >
              <Button
                size="2xs"
                h="6"
                px="2"
                variant="ghost"
                aria-pressed={preferredViewType === 'unified'}
                bg={preferredViewType === 'unified' ? 'ui.surface.card' : 'transparent'}
                boxShadow={preferredViewType === 'unified' ? 'xs' : 'none'}
                onClick={() => setPreferredViewType('unified')}
              >
                <Rows3 size={11} aria-hidden="true" />
                Unified
              </Button>
              <Button
                size="2xs"
                h="6"
                px="2"
                variant="ghost"
                aria-pressed={preferredViewType === 'split'}
                bg={preferredViewType === 'split' ? 'ui.surface.card' : 'transparent'}
                boxShadow={preferredViewType === 'split' ? 'xs' : 'none'}
                onClick={() => setPreferredViewType('split')}
              >
                <Columns2 size={11} aria-hidden="true" />
                Split
              </Button>
            </HStack>
          ) : null}
        </HStack>
      </Box>

      <VStack align="stretch" gap="1.5" flex="1" minH={0} overflowY="auto" p="2">
        {!noChanges ? (
          <GitHubPreviewSeoReview
            previewId={summary.preview.id}
            revision={summary.preview.revision}
            ready={
              ['ready', 'invalid'].includes(summary.preview.status) &&
              summary.preview.validationStatus !== 'pending'
            }
          />
        ) : null}
        {summary.files.map(change => {
          const expanded = expandedPath === change.path;
          const detail = details[`${summary.preview.revision}:${change.path}`]?.change;
          return (
            <Box
              key={change.path}
              borderWidth="1px"
              borderColor="ui.border.subtle"
              borderRadius="md"
              overflow="hidden"
              bg="ui.surface.card"
            >
              <Button
                variant="ghost"
                w="full"
                h="auto"
                minH="42px"
                px="2.5"
                py="2"
                justifyContent="flex-start"
                textAlign="left"
                borderRadius="0"
                aria-expanded={expanded}
                onClick={() => togglePath(change.path)}
              >
                {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <Box minW={0} flex="1">
                  <Text as="code" display="block" fontSize="2xs" fontWeight="650" truncate>
                    {change.path}
                  </Text>
                </Box>
                {change.kind === 'text' ? (
                  <Text fontSize="2xs" color="ui.text.tertiary" whiteSpace="nowrap">
                    +{change.additions} −{change.deletions}
                  </Text>
                ) : null}
                <Badge size="sm" variant="subtle" colorPalette={operationPalette(change.operation)}>
                  {change.kind === 'binary' ? 'Image' : operationLabel(change.operation)}
                </Badge>
              </Button>
              {expanded ? (
                loadingPath === change.path ? (
                  <Flex p="5" justify="center">
                    <Spinner size="sm" />
                  </Flex>
                ) : detail?.kind === 'binary' ? (
                  <BinaryChange change={detail} />
                ) : (
                  <TextChangeDiff change={detail || { ...change, diff: '' }} viewType={viewType} />
                )
              ) : null}
            </Box>
          );
        })}
        {noChanges ? (
          <Flex py="12" direction="column" align="center" textAlign="center">
            <FileDiff size={22} aria-hidden="true" />
            <Text mt="2" fontSize="sm" fontWeight="750">
              No changes yet
            </Text>
            <Text mt="1" fontSize="xs" color="ui.text.secondary">
              Changes made with Attune will collect here for one review.
            </Text>
          </Flex>
        ) : null}
      </VStack>

      <Box
        position="sticky"
        bottom="0"
        px="3"
        py="2.5"
        borderTopWidth="1px"
        borderColor="ui.border.default"
        bg="ui.surface.drawerHeader"
      >
        <HStack
          mb="2.5"
          gap="2"
          px="2.5"
          py="2"
          borderWidth="1px"
          borderColor="status.info.border"
          borderRadius="md"
          bg="status.info.bg"
          color="status.info.fg"
        >
          <ShieldCheck size={15} aria-hidden="true" />
          <Text fontSize="xs" fontWeight="650">
            Safe to submit — your live site will not change. Publishing requires a separate
            approval.
          </Text>
        </HStack>
        <HStack justify="space-between" gap="4" align="center">
          <HStack minW={0} gap="2" align="center">
            {currentPullRequest ? (
              <CheckCircle2 size={14} aria-hidden="true" />
            ) : (
              <GitPullRequest size={14} aria-hidden="true" />
            )}
            <Box minW={0}>
              <Text fontSize="2xs" fontWeight="700" truncate>
                {blockedReason || `${summary.summary.files} files ready for review`}
              </Text>
            </Box>
          </HStack>
          <Button
            flexShrink={0}
            minW="120px"
            size="sm"
            onClick={() => void deliver()}
            loading={delivering}
            disabled={
              noChanges ||
              buildBlocked ||
              currentPullRequest ||
              reviewExecuting ||
              (pendingRequest && !summary.permissions.canDeliver)
            }
          >
            {currentPullRequest
              ? 'GitHub is up to date'
              : pendingRequest && !summary.permissions.canDeliver
                ? 'Sent for approval'
                : actionLabel}
          </Button>
        </HStack>
      </Box>
    </Flex>
  );
}
